import { supabase } from '../lib/supabase';
import type { RiData } from '../lib/ecfRI';

export type { RiData };

/**
 * Cliente del API de facturación electrónica.
 *
 * Habla con el backend Express (`backend/`), no con Supabase directamente: el certificado digital
 * vive cifrado y su llave privada no debe pasar nunca por el navegador. En desarrollo, Vite
 * proxea /api/ecf al puerto 5051; en producción lo hace nginx.
 */

export type EcfEnvironment = 'test' | 'cert' | 'prod';
export type CertStatus = 'valid' | 'expired' | 'none';

export interface ServiceUrls {
  hostPath: string;
  rnc: string;
  recepcion: string;
  aprobacion: string;
  autenticacion: string;
}

export interface EcfConfig {
  environment: EcfEnvironment;
  emisor_name: string | null;
  emisor_rnc: string | null;
  notify_email: string | null;
  cert_status: CertStatus;
  cert_subject: string | null;
  cert_expires_at: string | null;
  certification_step: number;
  service_urls: ServiceUrls | null;
}

export interface UploadCertResult {
  subject: string;
  validFrom: string;
  validTo: string;
  expired: boolean;
  config: EcfConfig;
}

export interface TestConnectionResult {
  environment: EcfEnvironment;
  /** Nombre del ambiente tal como lo llama la DGII: TesteCF, CerteCF o eCF. */
  ambienteLabel: string;
  expedido: string | null;
  expira: string | null;
  ms: number;
}

export interface EcfSequence {
  id: string;
  doc_type: string;
  range_from: number;
  range_to: number;
  next_value: number;
  expires_at: string | null;
  active: boolean;
  alert_threshold: number;
  /** Calculados por el backend, para no repetir la aritmética en la UI. */
  remaining: number;
  low: boolean;
  expired: boolean;
}

export interface CreateSequenceInput {
  docType: string;
  rangeFrom: number;
  rangeTo: number;
  nextValue?: number;
  expiresAt?: string | null;
  active?: boolean;
  alertThreshold?: number;
}

export type UpdateSequenceInput = Partial<Omit<CreateSequenceInput, 'docType' | 'rangeFrom'>>;

// ─── Certificación (Paso 2: set de pruebas) ──────────────────────────────────

export type CertCaseStatus =
  | 'pendiente'
  | 'listo'
  | 'enproceso'
  | 'aceptado'
  | 'rechazado'
  | 'error';

export interface CertCaseResult {
  encf: string;
  doc_type: string;
  /** 'ecf' = comprobante íntegro · 'rfce' = resumen de consumo (< 250 mil) */
  send_via: 'ecf' | 'rfce';
  status: CertCaseStatus;
  track_id: string | null;
  security_code: string | null;
  message: string | null;
  has_xml: boolean;
  total?: string;
}

export interface CertPreview {
  environment: string;
  emisorRnc: string;
  razonSocial: string;
  total: number;
  ecfCount: number;
  rfceCount: number;
  cases: CertCaseResult[];
  yaEnviados: number;
  /** Comprobantes por tipo; los 32 < 250 mil van bajo la clave '32rfce', como los cuenta el portal. */
  porTipo: Record<string, number>;
  /** Número desde el que se renumeraron los e-NCF (solo en la simulación del paso 4). */
  desde?: number;
}

export interface CertRunResult {
  environment: string;
  enviados: number;
  saltados: number;
  fallidos: number;
  cases: CertCaseResult[];
}

/** Plan de reintento del paso 4: qué casos se reenvían y con qué e-NCF nuevos. */
export interface SimRetryPlan {
  desde: number;
  encfs: string[];
  anteriores: { encf: string; message: string | null }[];
}

export interface SimRetryResponse {
  plan: SimRetryPlan;
  /** null cuando solo se pidió el plan, sin ejecutar. */
  run: CertRunResult | null;
}

// ─── Certificación (Paso 3: aprobaciones comerciales) ────────────────────────

/** No hay 'enproceso': la DGII responde la aprobación comercial en el acto. */
export type ApprovalStatus = 'pendiente' | 'listo' | 'aceptado' | 'rechazado' | 'error';

export interface ApprovalResult {
  encf: string;
  rnc_emisor: string | null;
  monto: string | null;
  /** '1' aprobar · '2' rechazar, tal como viene en el set. */
  estado_set: string | null;
  status: ApprovalStatus;
  message: string | null;
  has_xml: boolean;
}

export interface ApprovalPreview {
  environment: string;
  compradorRnc: string;
  total: number;
  aprobaciones: number;
  rechazos: number;
  cases: ApprovalResult[];
  yaEnviadas: number;
  /** RNC comprador del set que no son el nuestro: el archivo sería de otra empresa. */
  rncAjenos: string[];
}

export interface ApprovalRunResult {
  environment: string;
  enviadas: number;
  saltadas: number;
  fallidas: number;
  cases: ApprovalResult[];
}

// ─── Certificación (Paso 5: representación impresa) ──────────────────────────

/** Una casilla del portal. Son 11 y no 10 porque el consumo se parte por el umbral de 250 mil. */
export interface RiSlot {
  clave: string;
  label: string;
  docType: string;
  sendVia: 'ecf' | 'rfce';
  /** e-NCF propuesto, o null si todavía no hay uno aceptado de ese tipo. */
  encf: string | null;
  opciones: string[];
}

export interface ServiceCheckRow {
  key: 'autenticacion' | 'recepcion' | 'aprobacion';
  label: string;
  url: string;
  ok: boolean;
  status: number | null;
  ms: number;
  detail: string;
}

export interface ServiceCheckResult {
  ok: boolean;
  rows: ServiceCheckRow[];
}

export interface UpdateConfigInput {
  environment?: EcfEnvironment;
  emisorName?: string | null;
  emisorRnc?: string | null;
  notifyEmail?: string | null;
  certificationStep?: number;
}

// ─── Operación real: emitidos, recibidos y panel ─────────────────────────────
// Estas son las formas que devuelve el backend, así que los campos que vienen del servidor
// conservan snake_case. No confundir con la certificación: aquella tiene sus propios tipos
// (CertCaseResult, ApprovalResult) porque son envíos de prueba con e-NCF que fija la DGII.

export type EstadoDgii = 'pendiente' | 'enproceso' | 'aceptado' | 'rechazado' | 'anulado';
export type EstadoComercial = 'pendiente' | 'aceptado' | 'rechazado';
/** 'pendiente' = decidido pero sin comunicar · 'enviado' = la DGII lo aceptó · 'fallido' = salió y no entró. */
export type EstadoAcecf = 'pendiente' | 'enviado' | 'fallido';

export interface EcfDoc {
  id: string;
  encf: string;
  doc_type: string;
  customer_name: string | null;
  customer_rnc: string | null;
  total: number;
  fecha_emision: string | null;
  send_via: 'ecf' | 'rfce';
  status: EstadoDgii;
  track_id: string | null;
  security_code: string | null;
  dgii_message: string | null;
  commercial_status: EstadoComercial;
  commercial_reason: string | null;
  has_xml: boolean;
  sent_at: string | null;
  created_at: string;
}

export interface EmittedFilters {
  estado?: EstadoDgii;
  docType?: string;
  desde?: string;
  hasta?: string;
  limit?: number;
}

export interface RegisterEmittedInput {
  encf: string;
  docType: string;
  customerName?: string | null;
  customerRnc?: string | null;
  total: number;
  fechaEmision?: string | null;
  /** XML ya firmado. Sin él el comprobante queda registrado pero no se puede enviar. */
  xmlSigned?: string | null;
  source?: string | null;
  sourceId?: string | null;
}

export interface EcfRecibido {
  id: string;
  encf: string;
  doc_type: string;
  issuer_rnc: string | null;
  issuer_name: string | null;
  total: number;
  fecha_emision: string | null;
  received_at: string;
  /** Acuse que devolvimos al recibirlo: '0' recibido · '1' no recibido. */
  arecf_status: string | null;
  arecf_code: string | null;
  commercial_status: EstadoComercial;
  commercial_reason: string | null;
  commercial_at: string | null;
  acecf_status: EstadoAcecf;
  acecf_message: string | null;
  acecf_at: string | null;
}

export interface EcfSummary {
  ambiente: EcfEnvironment;
  emitidos: {
    total: number;
    pendiente: number;
    enproceso: number;
    aceptado: number;
    rechazado: number;
    anulado: number;
    hoy: number;
    rechazadosPorCliente: number;
  };
  recibidos: { total: number; pendientes: number; acecfFallidos: number };
  secuencias: {
    bajas: Array<{ doc_type: string; remaining: number }>;
    vencidas: Array<{ doc_type: string; expires_at: string | null }>;
    sinRango: string[];
  };
  certificado: {
    status: CertStatus;
    expiresAt: string | null;
    diasParaVencer: number | null;
  };
}

export interface ProcessPendingResult {
  emitidos: { enviados: number; fallidos: number; sinXml: number; refrescados: number };
  acecf: { enviados: number; fallidos: number };
}

// ─── Padrón de la DGII ───────────────────────────────────────────────────────

export interface RncRecord {
  rnc: string;
  name: string;
  trade_name: string | null;
  activity: string | null;
  status: string | null;
  regime: string | null;
  /** Falso cuando el contribuyente no está ACTIVO en el padrón. */
  active: boolean;
}

// ─── Emisión desde una venta ─────────────────────────────────────────────────

export interface EmitItemInput {
  name: string;
  quantity: number;
  /** Precio unitario BASE, sin ITBIS. */
  unitPrice: number;
  /** 0.18 o 18: el backend normaliza ambas. */
  taxRate: number;
  isService?: boolean;
}

export interface EmitInput {
  docType: '31' | '32';
  items: EmitItemInput[];
  discountTotal?: number;
  paymentMethod?: string;
  customerRnc?: string | null;
  customerName?: string | null;
  source?: string;
  sourceId?: string | null;
}

/**
 * Importes y datos del emisor TAL COMO SE DECLARARON. El ticket imprime esto, no lo que calculó
 * el carrito: la representación impresa tiene que decir exactamente lo mismo que el comprobante.
 */
export interface EmitRi {
  /** 'test' | 'cert' | 'prod'. El ticket avisa cuando no es producción. */
  environment: string;
  razonSocial: string;
  rnc: string;
  direccion: string | null;
  fechaEmision: string;
  fechaFirma: string;
  docType: string;
  encfExpiration: string | null;
  montoGravadoTotal: string;
  totalItbis: string;
  montoTotal: string;
  lineas: Array<{ nombre: string; cantidad: number; precio: string; itbis: string; valor: string }>;
}

export interface EmitResult {
  doc: EcfDoc;
  qrUrl: string | null;
  securityCode: string | null;
  ri: EmitRi;
}

export interface InboundLogRow {
  id: string;
  created_at: string;
  kind: 'semilla' | 'token' | 'recepcion' | 'aprobacion';
  encf: string | null;
  http_status: number;
  ok: boolean;
  detail: string | null;
  remote_ip: string | null;
}

const BASE = '/api/ecf';

async function request<T>(path: string, init: RequestInit = {}): Promise<T> {
  const { data } = await supabase.auth.getSession();
  const token = data.session?.access_token;
  if (!token) throw new Error('Tu sesión expiró. Vuelve a iniciar sesión.');

  const res = await fetch(`${BASE}${path}`, {
    ...init,
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${token}`,
      ...(init.headers || {}),
    },
  });

  // El backend responde {ok, data} o {ok:false, error}. Si cae antes de llegar al handler
  // (proxy apagado, 502) el cuerpo no es JSON, así que se distingue ese caso.
  let body: any = null;
  try {
    body = await res.json();
  } catch {
    throw new Error(
      res.status === 404 || res.status === 502
        ? 'No se pudo contactar el servicio de facturación electrónica. ¿Está corriendo el backend?'
        : `Error del servidor (HTTP ${res.status})`
    );
  }

  if (!res.ok || !body?.ok) {
    throw new Error(body?.error || `Error del servidor (HTTP ${res.status})`);
  }
  return body.data as T;
}

/** Lee el archivo como base64 sin el prefijo `data:...;base64,` que añade readAsDataURL. */
function fileToBase64(file: File): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => {
      const result = String(reader.result || '');
      const comma = result.indexOf(',');
      resolve(comma >= 0 ? result.slice(comma + 1) : result);
    };
    reader.onerror = () => reject(new Error('No se pudo leer el archivo'));
    reader.readAsDataURL(file);
  });
}

/**
 * Descarga un XML firmado. No pasa por `request` porque la respuesta es XML, no el sobre JSON.
 */
async function descargarXml(path: string, fileName: string): Promise<void> {
  const { data } = await supabase.auth.getSession();
  const token = data.session?.access_token;
  if (!token) throw new Error('Tu sesión expiró. Vuelve a iniciar sesión.');

  const res = await fetch(`${BASE}${path}`, {
    headers: { Authorization: `Bearer ${token}` },
  });
  if (!res.ok) throw new Error('No se pudo descargar el XML');

  const blob = await res.blob();
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = fileName;
  a.click();
  URL.revokeObjectURL(url);
}

export const ecfService = {
  getConfig: () => request<EcfConfig>('/config'),

  updateConfig: (input: UpdateConfigInput) =>
    request<EcfConfig>('/config', { method: 'PUT', body: JSON.stringify(input) }),

  async uploadCertificate(file: File, password: string): Promise<UploadCertResult> {
    const fileBase64 = await fileToBase64(file);
    return request<UploadCertResult>('/certificate', {
      method: 'POST',
      body: JSON.stringify({ fileBase64, password }),
    });
  },

  deleteCertificate: () => request<EcfConfig>('/certificate', { method: 'DELETE' }),

  testConnection: () => request<TestConnectionResult>('/test-connection', { method: 'POST' }),

  /** Comprueba desde el servidor que las URLs registradas en el portal responden. */
  checkServiceUrls: () => request<ServiceCheckResult>('/services/check', { method: 'POST' }),

  // ─── Panel ─────────────────────────────────────────────────────────────────

  getSummary: () => request<EcfSummary>('/summary'),

  /** Reintenta de una vez los envíos que no salieron y las aprobaciones sin comunicar. */
  processPending: () => request<ProcessPendingResult>('/process-pending', { method: 'POST' }),

  // ─── Padrón de la DGII ─────────────────────────────────────────────────────

  /** RNC exacto. Devuelve null si no está en el padrón, que es un resultado normal. */
  lookupRnc: (rnc: string) => request<RncRecord | null>(`/rnc/${encodeURIComponent(rnc)}`),

  searchRnc: (q: string, limit = 15) =>
    request<RncRecord[]>(`/rnc/search?q=${encodeURIComponent(q)}&limit=${limit}`),

  padronInfo: () => request<{ total: number; updatedAt: string | null }>('/rnc/padron'),

  // ─── Emisión desde una venta ───────────────────────────────────────────────

  /**
   * Emite el comprobante de una venta. El servidor reserva el e-NCF, firma, GUARDA y después
   * envía, en ese orden: si la DGII no responde, el comprobante queda registrado como pendiente
   * y se reintenta desde Emitidos en vez de perderse con el e-NCF ya consumido.
   */
  emit: (input: EmitInput) =>
    request<EmitResult>('/emit', { method: 'POST', body: JSON.stringify(input) }),

  // ─── e-CF emitidos ─────────────────────────────────────────────────────────

  listEmitted: (filters: EmittedFilters = {}) => {
    const qs = new URLSearchParams();
    Object.entries(filters).forEach(([k, v]) => {
      if (v !== undefined && v !== null && v !== '') qs.set(k, String(v));
    });
    const query = qs.toString();
    return request<EcfDoc[]>(`/emitted${query ? `?${query}` : ''}`);
  },

  /**
   * Deja constancia de un comprobante emitido. Se llama ANTES de enviarlo a la DGII: si la
   * conexión se cae a mitad del envío, el e-NCF ya consumido queda registrado y se puede
   * reintentar desde la pantalla en vez de desaparecer sin rastro.
   */
  registerEmitted: (input: RegisterEmittedInput) =>
    request<EcfDoc>('/emitted', { method: 'POST', body: JSON.stringify(input) }),

  sendEmitted: (id: string) => request<EcfDoc>(`/emitted/${id}/send`, { method: 'POST' }),

  /** Consulta el veredicto de un comprobante en proceso. Sin `id` refresca todos. */
  refreshEmitted: (id?: string) =>
    request<EcfDoc[]>(id ? `/emitted/${id}/status` : '/emitted-status', { method: 'POST' }),

  downloadEmittedXml: (id: string, encf: string) =>
    descargarXml(`/emitted/${id}/xml`, `${encf}.xml`),

  /** Datos ya resueltos del comprobante; el PDF lo dibuja `src/lib/ecfRI.ts`. */
  emittedRiData: (id: string) => request<RiData>(`/emitted/${id}/ri`),

  // ─── e-CF recibidos ────────────────────────────────────────────────────────

  listReceived: (estado?: EstadoComercial) =>
    request<EcfRecibido[]>(`/received${estado ? `?estado=${estado}` : ''}`),

  /** Guarda la decisión comercial y envía el ACECF al emisor a través de la DGII. */
  decideReceived: (id: string, decision: 'aceptado' | 'rechazado', motivo?: string) =>
    request<EcfRecibido>(`/received/${id}/decision`, {
      method: 'POST',
      body: JSON.stringify({ decision, motivo: motivo ?? null }),
    }),

  /** Reintenta una decisión ya tomada cuyo ACECF no llegó a salir. */
  resendAcecf: (id: string) => request<EcfRecibido>(`/received/${id}/resend`, { method: 'POST' }),

  /** `tipo`: 'ecf' el comprobante del emisor · 'arecf' nuestro acuse · 'acecf' nuestra aprobación. */
  downloadReceivedXml: (id: string, encf: string, tipo: 'ecf' | 'arecf' | 'acecf' = 'ecf') =>
    descargarXml(
      `/received/${id}/xml?tipo=${tipo}`,
      `${tipo === 'ecf' ? '' : `${tipo.toUpperCase()}-`}${encf}.xml`
    ),

  // ─── Webservices entrantes ─────────────────────────────────────────────────

  inboundLog: (limit = 100) => request<InboundLogRow[]>(`/inbound/log?limit=${limit}`),

  listSequences: () => request<EcfSequence[]>('/sequences'),

  createSequence: (input: CreateSequenceInput) =>
    request<EcfSequence>('/sequences', { method: 'POST', body: JSON.stringify(input) }),

  updateSequence: (id: string, input: UpdateSequenceInput) =>
    request<EcfSequence>(`/sequences/${id}`, { method: 'PATCH', body: JSON.stringify(input) }),

  deleteSequence: (id: string) =>
    request<{ id: string }>(`/sequences/${id}`, { method: 'DELETE' }),

  // ─── Certificación ─────────────────────────────────────────────────────────

  certXsdStatus: () => request<{ tipos: string[]; total: number }>('/cert/xsd-status'),

  certResults: () => request<CertCaseResult[]>('/cert/results'),

  /** Arma y firma el set sin enviar nada. Sirve para validar antes de quemar e-NCF. */
  async certPreview(file: File): Promise<CertPreview> {
    const fileBase64 = await fileToBase64(file);
    return request<CertPreview>('/cert/preview', {
      method: 'POST',
      body: JSON.stringify({ fileBase64 }),
    });
  },

  /** Envía a la DGII. Irreversible: consume los e-NCF. */
  async certRun(file: File, only?: string[]): Promise<CertRunResult> {
    const fileBase64 = await fileToBase64(file);
    return request<CertRunResult>('/cert/run', {
      method: 'POST',
      body: JSON.stringify({ fileBase64, only }),
    });
  },

  certRefreshStatus: () => request<CertCaseResult[]>('/cert/status', { method: 'POST' }),

  certReset: () => request<{ reset: boolean }>('/cert/results', { method: 'DELETE' }),

  // ─── Certificación · Paso 4: simulación con datos propios ──────────────────
  // Mismo motor y mismos tipos que el paso 2; lo que cambia es que los e-NCF los genera el
  // sistema desde `desde`, y el emisor del comprobante es el real de la empresa.

  simResults: () =>
    request<{ cases: CertCaseResult[]; desdeSugerido: number }>('/cert/sim/results'),

  async simPreview(file: File, desde?: number): Promise<CertPreview> {
    const fileBase64 = await fileToBase64(file);
    return request<CertPreview>('/cert/sim/preview', {
      method: 'POST',
      body: JSON.stringify({ fileBase64, desde }),
    });
  },

  /** `desde` es obligatorio: son los números que se vieron en la vista previa. */
  async simRun(file: File, desde: number, only?: string[]): Promise<CertRunResult> {
    const fileBase64 = await fileToBase64(file);
    return request<CertRunResult>('/cert/sim/run', {
      method: 'POST',
      body: JSON.stringify({ fileBase64, desde, only }),
    });
  },

  /**
   * Reintento de los rechazados con e-NCF nuevos. `desde` es el bloque desde el que se renumera,
   * no el número del comprobante que falló: el servidor decide qué casos del set corresponden.
   * Sin `ejecutar` solo devuelve el plan.
   */
  async simRetry(file: File, desde?: number, ejecutar = false): Promise<SimRetryResponse> {
    const fileBase64 = await fileToBase64(file);
    return request<SimRetryResponse>('/cert/sim/retry', {
      method: 'POST',
      body: JSON.stringify({ fileBase64, desde, ejecutar }),
    });
  },

  simRefreshStatus: () => request<CertCaseResult[]>('/cert/sim/status', { method: 'POST' }),

  simReset: () => request<{ reset: boolean }>('/cert/sim/results', { method: 'DELETE' }),

  simDownloadXml: (encf: string) =>
    descargarXml(`/cert/sim/xml?encf=${encodeURIComponent(encf)}`, `${encf}.xml`),

  // ─── Certificación · Paso 5: representación impresa ────────────────────────

  riPlan: () => request<RiSlot[]>('/cert/ri/plan'),

  /** Datos ya resueltos del comprobante; el PDF lo dibuja `src/lib/ecfRI.ts`. */
  riData: (encf: string) => request<RiData>(`/cert/ri/data?encf=${encodeURIComponent(encf)}`),

  /**
   * Descarga el XML firmado. No pasa por `request` porque la respuesta es XML, no el sobre JSON.
   * Hace falta para subir a mano al portal las facturas de consumo íntegras.
   */
  certDownloadXml: (encf: string) => descargarXml(`/cert/xml?encf=${encodeURIComponent(encf)}`, `${encf}.xml`),

  // ─── Certificación · Paso 3: aprobaciones comerciales ──────────────────────

  approvalResults: () => request<ApprovalResult[]>('/cert/approvals/results'),

  /** Arma y firma las aprobaciones sin enviarlas. */
  async approvalPreview(file: File): Promise<ApprovalPreview> {
    const fileBase64 = await fileToBase64(file);
    return request<ApprovalPreview>('/cert/approvals/preview', {
      method: 'POST',
      body: JSON.stringify({ fileBase64 }),
    });
  },

  /** Envía los ACECF a la DGII. La respuesta es inmediata, sin trackId que consultar después. */
  async approvalRun(file: File, only?: string[]): Promise<ApprovalRunResult> {
    const fileBase64 = await fileToBase64(file);
    return request<ApprovalRunResult>('/cert/approvals/run', {
      method: 'POST',
      body: JSON.stringify({ fileBase64, only }),
    });
  },

  approvalReset: () => request<{ reset: boolean }>('/cert/approvals/results', { method: 'DELETE' }),

  approvalDownloadXml: (encf: string) =>
    descargarXml(`/cert/approvals/xml?encf=${encodeURIComponent(encf)}`, `ACECF-${encf}.xml`),
};
